if (!customElements.get('shc-cart-recommendations')) {
  customElements.define('shc-cart-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-shc-rec-direction]');
        if (!button || button.disabled || !this.rail) return;
        const cards = Array.from(this.rail.children);
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        const step = (cards[0]?.getBoundingClientRect().width || this.rail.clientWidth) + gap;
        const max = this.maxScroll();
        const direction = Number(button.dataset.shcRecDirection);
        const next = Math.max(0, Math.min(max, this.rail.scrollLeft + direction * step));
        this.rail.scrollTo({ left: next, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.events.signal });
      this.bindRail();
      this.seedObserver = new MutationObserver(() => this.load());
      this.seedObserver.observe(this, { attributes: true, attributeFilter: ['data-product-id', 'data-cart-product-ids'] });
      const drawer = this.closest('cart-drawer');
      if (drawer) {
        this.observer = new MutationObserver(() => {
          if (drawer.classList.contains('active')) {
            this.updateControls();
            this.load();
          }
        });
        this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        if (drawer.classList.contains('active')) queueMicrotask(() => this.load());
      } else {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { this.load(); this.observer.disconnect(); }
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.seedObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.events?.abort();
      this.railEvents?.abort();
      this.controller?.abort();
    }

    bindRail() {
      this.railEvents?.abort();
      this.resizeObserver?.disconnect();
      this.rail = this.querySelector('.shc-recommendations__rail');
      if (!this.rail) return;
      this.railEvents = new AbortController();
      this.rail.addEventListener('scroll', () => this.updateControls(), { passive: true, signal: this.railEvents.signal });
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.rail);
      this.updateControls();
    }

    maxScroll() {
      const first = this.rail?.firstElementChild;
      const last = this.rail?.lastElementChild;
      if (!first || !last) return 0;
      const contentWidth = last.getBoundingClientRect().right - first.getBoundingClientRect().left;
      return Math.max(0, Math.min(this.rail.scrollWidth - this.rail.clientWidth, contentWidth - this.rail.clientWidth));
    }

    updateControls() {
      if (!this.rail) return;
      const max = this.maxScroll();
      const left = Math.max(0, this.rail.scrollLeft);
      const previous = this.querySelector('[data-shc-rec-direction="-1"]');
      const next = this.querySelector('[data-shc-rec-direction="1"]');
      if (previous) previous.disabled = left <= 2;
      if (next) next.disabled = max <= 2 || left >= max - 2;
    }

    async load() {
      const productId = this.dataset.productId;
      const key = `${productId}:${this.dataset.cartProductIds || ''}`;
      if (!productId || this.loadedKey === key || this.pendingKey === key) return;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.pendingKey = key;
      try {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('section_id', 'shc-cart-recommendations');
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || this.pendingKey !== key || controller.signal.aborted) return;
        const content = html.querySelector('[data-shc-recommendations-content]');
        if (content) {
          const fallback = this.querySelector('[data-shc-recommendations-content]');
          const rail = content.querySelector('.shc-recommendations__rail');
          const excluded = new Set((this.dataset.cartProductIds || '').split(','));
          excluded.add(productId);
          const candidates = Array.from(content.querySelectorAll('[data-shc-recommendation-id]'));
          if (fallback?.dataset.shcFamily === content.dataset.shcFamily) {
            candidates.push(...fallback.querySelectorAll('[data-shc-recommendation-id]'));
          }
          const picks = [];
          for (const card of candidates) {
            const id = card.dataset.shcRecommendationId;
            if (excluded.has(id)) continue;
            excluded.add(id);
            picks.push(card);
            if (picks.length === 8) break;
          }
          if (rail && picks.length) {
            rail.replaceChildren(...picks);
            if (this.dataset.headingId) {
              content.setAttribute('aria-labelledby', this.dataset.headingId);
              content.querySelector('h3').id = this.dataset.headingId;
            }
            this.replaceChildren(content);
            this.bindRail();
          }
        }
        this.loadedKey = key;
      } catch (error) {
        if (error.name !== 'AbortError') this.updateControls();
      } finally {
        if (this.pendingKey === key) this.pendingKey = null;
      }
    }
  });
}
